import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Input, Select, Button, SearchableSelect, IconAlertTriangle } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * DocumentUploadModal — file picker + base64 upload to
 * POST /api/documents/upload.
 *
 * Server accepts up to ~5MB after base64 decoding (per
 * src/routes/documents.js MAX_FILE_SIZE_BASE64). Client enforces the
 * same limit pre-upload to avoid wasted round-trips.
 *
 * Phase 11 Block 11 PR 4 (2026-05-12).
 */

interface DocumentUploadModalProps {
  open: boolean;
  onClose: () => void;
  defaultContext?: {
    client_id?: string;
    property_id?: string;
    parcel_id?: string;
    assessor_id?: string;
    collector_id?: string;
    filed_return_id?: string;
  };
  // Called after a successful upload (before onClose) with the documents the server
  // created, so a caller whose list query key is not namespaced under ['documents', …]
  // can invalidate its own, and a caller that reacts to a particular upload (an
  // operating statement's read figures) can find it.
  onUploaded?: (uploaded: UploadedDocument[]) => void;
}

/** The upload route's 201 body, as much of it as a caller has a use for. */
export interface UploadedDocument {
  id: string;
  name?: string;
  document_type?: string | null;
  property_id?: string | null;
  /** Present on an Operating Statement workbook the server could read (ATA part 4 §5). */
  extracted?: Record<string, unknown> | null;
  extraction_error?: string;
}

interface DocumentTypesResponse {
  values?: string[];
}

const MAX_BYTES = 5 * 1024 * 1024; // 5MB raw

/**
 * A backfile arrives as a folder, not a file — so upload takes a SET. Capped because
 * each document is base64'd in memory and POSTed individually; twenty 5MB files is
 * already 100MB of client-side work, and beyond that the honest answer is a different
 * mechanism (a drop folder), not a bigger number here.
 *
 * The server's bulk READ allows 200; this is the narrower of the two on purpose. Upload
 * is the interactive step a person waits through.
 */
const MAX_FILES = 20;

export function DocumentUploadModal({ open, onClose, defaultContext = {}, onUploaded }: DocumentUploadModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [files, setFiles] = useState<File[]>([]);
  // Per-file outcome of the last run. A partial success is the NORMAL case for a
  // backfile — one corrupt scan must not discard the nineteen that worked — so failures
  // are reported per file and the successes still stage.
  const [failures, setFailures] = useState<Array<{ name: string; reason: string }>>([]);
  const [name, setName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [year, setYear] = useState<string>(() => String(new Date().getFullYear()));

  const docTypesQuery = useQuery<string[] | DocumentTypesResponse>({
    queryKey: ['org-settings', 'document-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/document-types', signal),
    enabled: open,
  });

  const docTypes = normalizeValues(docTypesQuery.data);

  // The document types the firm auto-shares to the client portal on upload (the
  // client is notified) — read live from the org's config (item 2.3), replacing the
  // formerly-hardcoded set that drifted from document_auto_share_types by hand. The
  // SAME query key OrgSettings' auto-share editor invalidates, so a config change
  // refreshes this hint. Graceful: while loading / empty the set is empty → no hint.
  const autoShareQuery = useQuery<string[]>({
    queryKey: ['document-auto-share-types'],
    queryFn: ({ signal }) => api.get<string[]>('/api/document-auto-share-types', signal),
    enabled: open,
  });
  const autoShareSet = new Set(autoShareQuery.data ?? []);

  // Property picker — docs live at the property level but are housed on the
  // client, so when uploading from the CLIENT we let the user pick which active
  // property the doc belongs to. Skipped when a property/parcel is already the
  // upload context (the link is implied). Searchable (type-to-search) because a
  // client can have hundreds of properties — a plain scroll is unusable on mobile.
  const showPropertyPicker = !!defaultContext.client_id && !defaultContext.property_id && !defaultContext.parcel_id;
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const propertiesQuery = useQuery<Array<{ id: string; name: string | null; status: string | null }>>({
    queryKey: ['properties-for-upload', defaultContext.client_id],
    queryFn: ({ signal }) => api.get(`/api/properties?client_id=${defaultContext.client_id}`, signal),
    enabled: open && showPropertyPicker,
  });
  const propertyOptions = (propertiesQuery.data ?? [])
    .filter(p => (p.status ?? 'active') === 'active')
    .map(p => ({ value: p.id, label: p.name || p.id }));

  const mutation = useMutation<{ uploadedIds: string[]; uploaded: UploadedDocument[]; failed: Array<{ name: string; reason: string }> }, Error, void>({
    mutationFn: async () => {
      if (!files.length) throw new Error('No file selected');

      // SEQUENTIAL, not parallel. Each file is base64'd whole in memory, so twenty at
      // once is twenty simultaneous copies plus twenty in-flight bodies. Sequential also
      // means a failure is attributable to ONE file instead of a pile of racing rejections.
      const uploadedIds: string[] = [];
      const uploaded: UploadedDocument[] = [];
      const failed: Array<{ name: string; reason: string }> = [];

      for (const f of files) {
        try {
          if (f.size > MAX_BYTES) {
            throw new Error(`too large (${(f.size / 1024 / 1024).toFixed(1)}MB; max 5MB)`);
          }
          const file_data = await readFileAsBase64(f);
          const doc = await api.post<UploadedDocument>('/api/documents/upload', {
            filename: f.name,
            content_type: f.type || 'application/octet-stream',
            file_data,
            document_type: documentType || null,
            // A display name only means something for ONE file; for a set, each keeps
            // its own filename rather than twenty documents sharing one label.
            name: files.length === 1 ? (name || f.name) : f.name,
            year: year ? Number(year) : null,
            client_id: defaultContext.client_id || null,
            property_id: propertyId ?? defaultContext.property_id ?? null,
            parcel_id: defaultContext.parcel_id || null,
            assessor_id: defaultContext.assessor_id || null,
            collector_id: defaultContext.collector_id || null,
            filed_return_id: defaultContext.filed_return_id || null,
          });
          if (doc?.id) { uploadedIds.push(doc.id); uploaded.push(doc); }
        } catch (err) {
          failed.push({ name: f.name, reason: err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/, '') : 'upload failed' });
        }
      }

      // Every file failed → surface it as an error, not a hollow success.
      if (!uploadedIds.length) {
        throw new Error(failed[0] ? `${failed[0].name}: ${failed[0].reason}` : 'Nothing uploaded');
      }
      return { uploadedIds, uploaded, failed };
    },
    onSuccess: (data) => {
      const n = data.uploadedIds.length;
      setFailures(data.failed);
      toast({
        // A partial result is reported as one, not rounded up to success.
        variant: data.failed.length ? 'warning' : 'success',
        message: data.failed.length
          ? `Uploaded ${n} of ${n + data.failed.length}. ${data.failed.length} could not be uploaded.`
          : (n === 1 ? `Uploaded "${name || files[0]?.name}".` : `Uploaded ${n} documents.`),
      });
      queryClient.invalidateQueries({ queryKey: ['documents-list'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      // The Documents-list 'Client viewed' column reads a separate query — refresh
      // it too so an auto-shared upload shows 'Not viewed' rather than '—'.
      queryClient.invalidateQueries({ queryKey: ['documents-view-status'] });
      onUploaded?.(data.uploaded);
      setFiles([]);
      setName('');
      setDocumentType('');
      setPropertyId(null);
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    setFailures([]);
    // Truncate rather than reject the whole selection: someone who drags a folder of 60
    // scans should get the first 20 read, told plainly, not an error and nothing.
    const kept = picked.slice(0, MAX_FILES);
    setFiles(kept);
    if (kept.length === 1 && !name) setName(kept[0].name.replace(/\.[^.]+$/, ''));
    if (kept.length !== 1) setName('');
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  const canSubmit = files.length > 0 && !mutation.isPending;
  const oversize = files.filter(f => f.size > MAX_BYTES);

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title="Upload document"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
          >
            Upload
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={labelStyle}>File</span>
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            style={{
              width: '100%',
              padding: 'var(--sp-2)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-md)',
              background: 'var(--color-card)',
              color: 'var(--color-ink)',
              fontSize: 'var(--fs-md)',
              fontFamily: 'inherit',
            }}
            required
          />
          {files.length > 0 && (
            <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)' }}>
              {files.length > 1 && (
                <div style={{ color: 'var(--color-ink-3)', marginBottom: 'var(--sp-1)' }}>
                  {files.length} files selected{oversize.length > 0 && ` · ${oversize.length} over the 5MB limit`}
                </div>
              )}
              {files.map((f) => (
                <div key={f.name + f.size} style={{ color: f.size > MAX_BYTES ? 'var(--color-status-red)' : 'var(--color-ink-3)' }}>
                  {f.name} · {(f.size / 1024).toFixed(1)}KB · {f.type || 'unknown type'}
                  {f.size > MAX_BYTES && (
                    <> · <IconAlertTriangle size={12} style={{ verticalAlign: '-2px' }} /> exceeds 5MB limit</>
                  )}
                </div>
              ))}
              {files.length === MAX_FILES && (
                <div style={{ color: 'var(--color-ink-3)', marginTop: 'var(--sp-1)' }}>
                  Showing the first {MAX_FILES} — upload these, then select the rest.
                </div>
              )}
            </div>
          )}

          {/* A partial upload names what did not land. Silence here would read as
              "all twenty worked" when four did not. */}
          {failures.length > 0 && (
            <div style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--fs-xs)', color: 'var(--color-status-red)' }}>
              <div><IconAlertTriangle size={12} style={{ verticalAlign: '-2px' }} /> Not uploaded:</div>
              {failures.map(f => <div key={f.name}>{f.name} — {f.reason}</div>)}
            </div>
          )}
        </label>

        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={labelStyle}>Display name</span>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Defaults to filename" />
        </label>

        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={labelStyle}>Document type</span>
          <Select
            value={documentType}
            onChange={e => setDocumentType(e.target.value)}
            placeholder="— Select type —"
            options={docTypes.map(t => ({ value: t, label: t }))}
          />
          {autoShareSet.has(documentType) && (
            <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)', color: 'var(--color-status-green, #14532d)' }}>
              Shared automatically — this type is sent to the client portal and the client is notified on upload. No extra step needed.
            </div>
          )}
        </label>

        {showPropertyPicker && (
          <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
            <span style={labelStyle}>Property</span>
            <SearchableSelect
              options={propertyOptions}
              value={propertyId}
              onChange={setPropertyId}
              placeholder={propertiesQuery.isLoading ? 'Loading properties…' : 'Search properties…'}
              aria-label="Property"
            />
          </label>
        )}

        <label style={{ display: 'block' }}>
          <span style={labelStyle}>Tax year</span>
          <Input type="number" value={year} onChange={e => setYear(e.target.value)} />
        </label>

        {(defaultContext.client_id || defaultContext.property_id || defaultContext.parcel_id || defaultContext.assessor_id || defaultContext.collector_id || defaultContext.filed_return_id) && (
          <div style={{
            marginTop: 'var(--sp-3)',
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--color-ink-3)',
          }}>
            Attaching to:{' '}
            {defaultContext.client_id && <span>client </span>}
            {defaultContext.property_id && <span>property </span>}
            {defaultContext.parcel_id && <span>parcel </span>}
            {defaultContext.assessor_id && <span>assessor </span>}
            {defaultContext.collector_id && <span>collector </span>}
            {defaultContext.filed_return_id && <span>return </span>}
            (from current context)
          </div>
        )}
      </form>
    </Modal>
  );
}

function normalizeValues(data: string[] | { values?: string[] } | undefined): string[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.values || [];
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the `data:<mime>;base64,` prefix
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 'var(--sp-1)',
  fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-medium)',
  color: 'var(--color-ink)',
};
